import { Component, HostListener, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ConstantsService } from '@app/common/constants.service';
import { AgGridConfigurationService } from '@app/shared/services/ag-grid-configuration.service';
import { AlertService } from '@app/shared/services/alert.service';
import { LoaderService } from '@app/shared/services/loader.service';
import { ReleaseMgmtService } from '@app/shared/services/releaseMgmtService';
import { ColDef, GridOptions, ICellRendererParams, RowModelType } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { TableLazyLoadEvent } from 'primeng/table';
import { RollbackMigrationDetailsComponent } from '../rollback-migration-details/rollback-migration-details.component';
import { SliderComponent } from '@app/shared/shared-components/slider/slider.component';
import { AgGridModule } from 'ag-grid-angular';
import { MatOptionModule } from '@angular/material/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatFormFieldModule } from '@angular/material/form-field';
import { PageHeaderComponent } from '@app/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { UserAuthService } from '../../../shared/services/user-auth.service';
import moment from 'moment';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

/**
 * Represents the RollbackMigrationListComponent component.
 */
@Component({
    standalone: true,
    selector: 'app-rollback-migration-list',
    templateUrl: './rollback-migration-list.component.html',
    styleUrls: ['./rollback-migration-list.component.scss'],
    imports: [CommonModule, RollbackMigrationDetailsComponent, SliderComponent, AgGridModule, MatOptionModule, NgxMatSelectSearchModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, PageHeaderComponent, MatSelectModule, MatTooltipModule, MatIconModule]
})
/**
 * Represents the RollbackMigrationListComponent component.
 */
export class RollbackMigrationListComponent implements OnInit {
    infiniteInitialRowCount = 30;
    // Declaration of a string variable to store the page title
    pageTitle = 'Rollback Database';
    configEnvInfoMsg = 'Select Applicable Environment to view Rollback Status';

    // Declaration of a string variable to store the page subtitle
    pageSubTitle = 'Rollback service for host and tenant databases and view rollback status of ';
    // Declaration of a number variable to store the release ID
    releaseId = 0;
    // Declaration of a boolean variable to store the value of isEnvironmentSelected
    isEnvironmentSelected = false;
    // Declaration of an empty array to store databaseRollbackList
    databaseRollbackList = []
    // Declaration of an empty array to store filtered environments
    filteredEnvironments = [];
    // Declaration of an empty array to store all environments
    allEnvironments = [];
    // Declaration of an UntypedFormControl object for search filter control environments
    searchFilterControlEnvironments: UntypedFormControl = new UntypedFormControl();
    // Declaration of a string variable to store the selected environment Id
    selectedEnvironmentId = '';
    // Declaration of a string variable to store the select environment info message
    SelectEnvInfoMsg = 'Select environment to deploy the Release';
    //Default width for the column
    testWidth = 250;
    // Declaration of a GridOptions object
    gridOptions: GridOptions;
    // Declare the columnDefs variable
    columnDefs: any;
    // Declare the agGridColDef variable
    agGridColDef: any;
    // Set the initial page size for pagination
    pageSize = 20;
    // Initialize the selectedRow variable
    selectedRow: any = {};
    // Define the available page size options for pagination
    pageSizeList: number[] = [25, 50, 75, 100];
    // `api` is used to interact with the grid
    api: any;
    // `gridColumnApi` is used to interact with the grid's columns
    public gridColumnApi: any;
    // Declare the agGridRowData variable
    agGridRowData: any;
    // `overlayNoRowsTemplate` is the HTML that is displayed when the grid has no rows
    public overlayNoRowsTemplate = '<span style="font-weight: 400; color: #757575; ">No records found</span>';
    // `cacheBlockSize` is the number of rows fetched per block from the server
    cacheBlockSize: any = 25;
    // `maxBlocksInCache` is the maximum number of blocks allowed in the cache
    maxBlocksInCache: any = 1;
    // `blockLoadDebounceMillis` is the debounce time in milliseconds for block loading
    blockLoadDebounceMillis = 100;
    // Define the row model type
    rowModelType: RowModelType = 'serverSide';
    // Set the row height for the grid
    rowHeight = 45;
    // Set the style for the chip div
    styleChipDiv = 'width:100%; height: calc(100vh - 226px); flex: 1 1 auto; border: 1px solid #DFDFDF;';
    //Set the server side store type
    serverSideStoreType = 'partial'; // ag grid server side store type
    // Set the loading flag to false
    loading: boolean;
    //Represents the default column definition for the add-view-release-list component.
    public defaultColDef: ColDef = {
        //Specifies whether the column is sortable.
        sortable: true,
        //Specifies whether the column is resizable.
        resizable: true,
        //Specifies whether the column has a filter.
        filter: true,
        //Specifies whether the column can be used for row grouping.
        enableRowGroup: true,
        //Specifies whether the column can be used for value aggregation.
        enableValue: true,
        //Specifies whether the column is editable.
        editable: false,
        //Specifies whether the column height is automatically adjusted based on the content.
        autoHeight: false,
        //Specifies whether the column can be pinned.
        floatingFilter: false,
        //Set initial width for the column
        initialWidth: 200,
        //Set the column width to fill the remaining space
        wrapHeaderText: true,
        //Set the column width to fill the remaining space
        autoHeaderHeight: true
    };
    // Represents the showRollbackMigrationDetailsSlider variable.
    showRollbackMigrationDetailsSlider = false;
    // `gridDataParams` is used to store grid parameters
    gridDataParams: any;
    // Represents the selectedReleaseLegoRollbackStatusId variable.
    selectedReleaseLegoRollbackStatusId: any;
    //Slider description
    sliderDescription: any = 'Rollback Audit Details';
    //Slider id
    rollbackMigrationDetailsSliderId = 'rollbackMigrationDetailsSliderId'; // slider element id
    /**
     * Represents the RollbackMigrationListComponent.
     */
    constructor(public readonly agGridConfigurationService: AgGridConfigurationService,
        public releaseMgmtService: ReleaseMgmtService,
        public constantsService: ConstantsService, // Service for accessing application constants
        public route: ActivatedRoute,
        public alertService: AlertService, // Service for managing alerts
        public loader: LoaderService,
        public toastr: ToastrService,
        public readonly userAuthService: UserAuthService) {
    }

    ngOnInit() {
        // Call the getServicesAndEnvironments() method to fetch services and environments data
        this.getServicesAndEnvironments();

        // Subscribe to changes in the searchFilterControlEnvironments value
        this.searchFilterControlEnvironments.valueChanges.subscribe((value) => {
            // Filter the environments based on the search value
            this.filteredEnvironments = this.allEnvironments.filter((item) => item.environmentName.toLowerCase().includes(value.toLowerCase()));
        });



        this.agGridColDef =
        {
            tableName: 'rollBackData',
            column:
                [
                    {
                        columnName: 'serviceName',
                        headerName: 'Service',
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            buttons: ['clear'],
                            suppressSorting: false,
                            caseSensitive: false,
                            suppressSelectAll: false,
                            suppressMiniFilter: false,
                        },
                        flex: 1,
                        minWidth: 150,
                        maxWidth: 200,
                    }, {
                        columnName: 'rollbackInitiatedBy',
                        headerName: 'Initiated By',
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            buttons: ['clear'],
                            suppressSorting: false,
                            caseSensitive: false,
                            suppressSelectAll: false,
                            suppressMiniFilter: false,
                        },
                        flex: 1,
                        minWidth: 150,
                        maxWidth: 200,
                    },
                    {
                        columnName: 'rollbackInitiatedDatetime',
                        dataType: 'date',
                        headerName: 'Initiated On',
                        minWidth: 150,
                        maxWidth: 200,
                        field: 'rollbackInitiatedDatetime',
                        filter: 'agDateColumnFilter',
                        filterParams: {
                            buttons: ['clear'],
                            filterOptions: ['equals'],
                            defaultOption: 'equals',
                            suppressAndOrCondition: true,
                            comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
                                if (!cellValue) return -1;
                                const cellDate = new Date(cellValue);
                                if (
                                    cellDate.getFullYear() === filterLocalDateAtMidnight.getFullYear() &&
                                    cellDate.getMonth() === filterLocalDateAtMidnight.getMonth() &&
                                    cellDate.getDate() === filterLocalDateAtMidnight.getDate()
                                ) {
                                    return 0;
                                }
                                return -1;
                            }
                        },
                        tooltipValueGetter: (params: ICellRendererParams) => {
                            return params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm A') : '';
                        },
                        cellRenderer: (params: ICellRendererParams) => {
                            return params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm A') : '';
                        }
                    },
                    {
                        columnName: 'rollbackCompletedDatetime',
                        dataType: 'date',
                        headerName: 'Completed On',
                        minWidth: 150,
                        maxWidth: 200,
                        field: 'rollbackCompletedDatetime',
                        filter: 'agDateColumnFilter',
                        filterParams: {
                            buttons: ['clear'],
                            filterOptions: ['equals'],
                            defaultOption: 'equals',
                            suppressAndOrCondition: true,
                            comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
                                if (!cellValue) return -1;
                                const cellDate = new Date(cellValue);
                                if (
                                    cellDate.getFullYear() === filterLocalDateAtMidnight.getFullYear() &&
                                    cellDate.getMonth() === filterLocalDateAtMidnight.getMonth() &&
                                    cellDate.getDate() === filterLocalDateAtMidnight.getDate()
                                ) {
                                    return 0;
                                }
                                return -1;
                            }
                        },
                        tooltipValueGetter: (params: ICellRendererParams) => {
                            return params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm A') : '';
                        },
                        cellRenderer: (params: ICellRendererParams) => {
                            return params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm A') : '';
                        }
                    },
                    { columnName: 'ACTION', headerName: 'Action', dataType: 'string', columnType: 'string', cellStyle: 'left', sortable: false, width: 150, minWidth: 150, maxWidth: 150, editable: false },
                    { columnName: 'rollbackStatus', headerName: 'Status', dataType: 'string', columnType: 'string', cellStyle: 'left', sortable: false, width: 150, minWidth: 150, maxWidth: 150, editable: false },
                    {
                        columnName: 'failureComment',
                        headerName: 'Details',dataType: 'string',
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            buttons: ['clear'],
                            suppressSorting: false,
                            caseSensitive: false,
                            suppressSelectAll: false,
                            suppressMiniFilter: false,
                        },
                        flex: 1,
                        minWidth: 150,
                        maxWidth: 200,
                    },
                ]
        };

        // Added grid options
        this.gridOptions = {
            // onCellClicked
            onCellClicked: this.handleCellClick.bind(this),
        };
        //No pagination
        this.agGridConfigurationService.paginationOn = false;
        // `columnDefs` is set to the column definitions returned by the `getRollbackColumnDetails` method of `agGridConfigurationService`
        this.columnDefs = this.agGridConfigurationService.getRollbackColumnDetails(this.agGridColDef);
    }


    /**
     * Callback function triggered when the ag-Grid is ready.
     * @param params - The parameters passed to the function.
     */
    onGridReady = (params) => {
        if (params != undefined) {
            // `gridDataParams` is set to the parameters passed to the method
            this.gridDataParams = params;
            // `api` is set to the api property of the parameters, which is the ag-Grid API
            this.api = params.api;
            // `gridColumnApi` is set to the columnApi property of the parameters, which is the ag-Grid Column API
            this.gridColumnApi = params.api;
            // `datasource` is set to the result of the `getGridData` method with null as the argument
            const datasource = this.getDatabaseRollbackData(null);
            // The server-side datasource for the ag-Grid is set to `datasource`
            this.api?.setServerSideDatasource(datasource);
            this.api?.setSideBar(false);

            // Auto-size columns to fit content
            setTimeout(() => {
                this.api?.sizeColumnsToFit();
            }, 100);
        }
    }

    // `@HostListener` decorator is used to declare a DOM event to listen for, and provide a handler method to run when that event occurs
    @HostListener('window:resize', ['$event'])

    /**
     * Event handler for window resize event.
     * Adjusts the height of the element with id 'myGrid' and calls the `sizeColumnsToFit` method of the ag-Grid API to adjust column widths.
     */
    onResize() {
        // `h` is set to the inner height of the window minus 210 pixels, converted to a string with 'px' appended
        const h = window.innerHeight - 300 + 'px';
        // The height of the element with the id 'myGrid' is set to `h`
        document.querySelector<HTMLElement>('#gridRollback')!.style.height = h;
        // After a delay of 0 milliseconds, the `sizeColumnsToFit` method of the ag-Grid API is called to adjust the column widths to fit the grid's width
        setTimeout(() => {
            this.api.sizeColumnsToFit();
        }, 0);
    }

    // This method is triggered when a cell in the grid is clicked
    handleCellClick(event) {
        // If the clicked cell is in the 'ACTION' column     
        if (event.column.colId === 'ACTION' && (event.data.rollbackStatus === this.constantsService.RollbackStatusNotStarted || event.data.rollbackStatus === this.constantsService.RollbackStatusFailed)) {
            if (!this.userAuthService.checkPermission('TRIGGER.ROLLBACK.DATABASE')) {
                return; // Do nothing if permission is false
            }
            //Show confirmation to start rollback
            this.confirmRollbackAction(event.data.releaseEnvironmentMappingId, event.data.releaseServiceMappingId);
        }
        else if (event.column.colId === 'failureComment' && (event.data.failureComment != null && event.data.failureComment != '')) {
            //Close existing migration details
            this.closerollbackMigrationDetails();
            //Set selected release lego rollback status id
            this.selectedReleaseLegoRollbackStatusId = event.data.id;
            //Set showRollbackMigrationDetailsSlider to show slider 
            this.showRollbackMigrationDetailsSlider = true;
            //Ser slider description
            this.sliderDescription = event.data.serviceName + ' Rollback Audit Details';
            // show conversation History slider
            setTimeout(() => {
                const sliderElement = document.getElementById(this.rollbackMigrationDetailsSliderId);
                if (sliderElement) {
                    sliderElement.style.width = this.constantsService.sliderWidth70;
                }
            }, 200);
        }
    }

    /**
     * Retrieves the database rollback data for the ag-Grid.
     * @param event - The event object containing lazy load information.
     * @returns An object with a `getRows` method that sets the column definitions for the ag-Grid, makes a request to get the release details list, and sets the row data for the ag-Grid.
     */
    getDatabaseRollbackData(event: TableLazyLoadEvent) {
        return {
            // `getRows` is a method that sets the column definitions for the ag-Grid, makes a request to get the release details list, and sets the row data for the ag-Grid
            getRows: (params) => {
                // The column definitions for the ag-Grid are set to `columnDefs`
                this.api.setColumnDefs(this.columnDefs);
                // `inputRequest` is an object with a `requestParameters` property that is set to the stringified request parameters
                const requestObject = {
                    inputRequest: {
                        requestParameters: JSON.stringify(params.request)
                    },
                    releaseEnvironmentMappingId: this.selectedEnvironmentId,
                    releaseId: this.releaseId
                };
                // Show the spinner/loader
                this.setLoader();
                // Call the API to get release data by releaseId
                this.releaseMgmtService.apiPostDatabaseRollbackData(requestObject).subscribe({
                    next: (res) => {
                        if (res) {
                            this.agGridRowData = res.data;

                            // The `success` method of the parameters is called with an object that has `rowData` set to `agGridRowData` and `rowCount` set to the total count from the response
                            params.success({
                                rowData: this.agGridRowData,
                                rowCount: res.totalCount,
                            });
                        }
                    },
                    error: (err) => { this.toastr.error('Error while fetching release data', 'Error'); },
                    complete: () => {
                        // Hide the spinner/loader
                        this.setUnLoader();
                    }
                });
            }
        }
    }

    /**
     * Retrieves services and environments data for a specific release.
     */
    getServicesAndEnvironments() {
        // Show the spinner/loader
        this.loader.showSpinner.next(true);
        // Get the releaseId from the route parameter
        this.releaseId = Number(this.route.snapshot.paramMap.get('releaseId'));
        // Call the API to get release data by releaseId
        this.releaseMgmtService.apiGetReleaseDataById(this.releaseId).subscribe({
            next: (res) => {
                if (res) {
                    // Append the release name to the page subtitle
                    this.pageSubTitle += '<b>' + res.releaseName + '</b>';
                    // Set the filtered environments data from the response
                    this.allEnvironments = res.environmentsConfigMapping;
                    // Set the filtered environments data from the response
                    this.filteredEnvironments = this.allEnvironments;
                }
            },
            error: (err) => { this.toastr.error('Error while fetching release data', 'Error'); },
            complete: () => {
                // Hide the spinner/loader
                this.setUnLoader();
            }
        });
    }

    /**
     * Initiates a rollback for a specific release environment mapping and release service mapping.
     * 
     * @param releaseEnvironmentMappingId - The ID of the release environment mapping.
     * @param releaseServiceMappingId - The ID of the release service mapping.
     */
    rollbackMigration(releaseEnvironmentMappingId: any, releaseServiceMappingId: any) {
        //Set loader
        this.setLoader();
        //Call API to initiate rollback
        this.releaseMgmtService.apiPostInitiateRollback(releaseEnvironmentMappingId, releaseServiceMappingId).subscribe({
            next: (res) => {
                if (res) {
                    if (res.status.toLowerCase() == 'success') {
                        this.toastr.success('Rollback initiated successfully.', 'Success');
                    }
                    else {
                        this.showErrorMessageByCode(res.message.toUpperCase());
                    }
                    this.refreshData();
                }
            },
            error: (err) => { this.toastr.error('Error while initiating rollback', 'Error'); },
            complete: () => {
                // Hide the spinner/loader
                this.setUnLoader();
            }
        });
    }

    /**
     * Refreshes the data in the ag-Grid by setting the server-side datasource.
     */
    /* istanbul ignore next */
    refreshData() {
        // `datasource` is set to the result of the `getGridData` method with null as the argument
        const datasource = this.getDatabaseRollbackData(null);
        // The server-side datasource for the ag-Grid is set to `datasource`
        this.api.setServerSideDatasource(datasource);
    }


    /**
     * Displays an error message based on the provided message code.
     * 
     * @param {string} messageCode - The code representing the error message.
     * @returns {void}
     */
    showErrorMessageByCode(messageCode) {
        let errorMessage = 'Error while initiating rollback';
        // Check the message code and set the error message accordingly
        // The error message is displayed using the Toastr service
        //Check if message code is equal to no environment mapping found
        if (messageCode == 'NO_ENVIRONMENT_MAPPING_FOUND') {
            errorMessage = this.constantsService.NO_ENVIRONMENT_MAPPING_FOUND;
        }
        //Check if message code is equal to no release service mapping found
        else if (messageCode == 'NO_RELEASE_SERVICE_MAPPING_FOUND') {
            errorMessage = this.constantsService.NO_RELEASE_SERVICE_MAPPING_FOUND;
        }
        //Check if message code is equal to no release found
        else if (messageCode == 'NO_RELEASE_FOUND') {
            errorMessage = this.constantsService.NO_RELEASE_FOUND;
        }
        //Check if message code is equal to rollback job already in progress
        else if (messageCode == 'ROLLBACK_JOB_ALREADY_IN_PROGRESS') {
            errorMessage = this.constantsService.ROLLBACK_JOB_ALREADY_IN_PROGRESS;
        }
        //Check if message code is equal to specified rollback version not found
        else if (messageCode == 'SPECIFIED_ROLLBACK_VERSION_NOT_FOUND') {
            errorMessage = this.constantsService.SPECIFIED_ROLLBACK_VERSION_NOT_FOUND;
        }
        //Check if message code is equal to rollback file not found
        else if (messageCode == 'ROLLBACK_FILE_NOT_FOUND') {
            errorMessage = this.constantsService.ROLLBACK_FILE_NOT_FOUND;
        }
        //Check if message code is equal to missing url path
        else if (messageCode == 'MISSING_URL_PATH') {
            errorMessage = this.constantsService.Missing_Url_Path;
        }
        //Check if message code is equal to missing client key or api key
        else if (messageCode == 'MISSING_CLIENTKEY_OR_APIKEY') {
            errorMessage = this.constantsService.Missing_ClientKey_Or_ApiKey;
        }
        //Check if message code is equal to invalid token
        else if (messageCode == 'INVALID_TOKEN') {
            errorMessage = this.constantsService.InvalidToken;
        }
        // The error message is displayed using the Toastr service
        this.toastr.error(errorMessage, 'Error');
    }

    /**
     * Confirms the rollback action and initiates the rollback database process.
     */
    confirmRollbackAction(releaseEnvironmentMappingId: any, releaseServiceMappingId: any) {
        this.alertService.buildDialog().fire({
            title: 'Are you sure?',
            text: 'Do you want to proceed initiating rollback database?',
            showCancelButton: true,
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            allowOutsideClick: false,
            icon: this.alertService.WarningIcon
        }).then((result) => {
            if (result.value) {
                //Set loader
                this.setLoader();
                //Call API to initiate rollback
                this.rollbackMigration(releaseEnvironmentMappingId, releaseServiceMappingId);
            }
        });
    }
    /**
     * Method to handle the change in the selected environment.
     */
    onEnvironmentChange(selectedEnvId: any) {
        // Check if "selectedEnvId" is not null, not 0, and not undefined
        if (selectedEnvId) {
            // Set the "isEnvironmentSelected" variable to true
            this.isEnvironmentSelected = true;
            // Set the "selectedEnvironmentId" variable to the value of "selectedEnvId"
            this.selectedEnvironmentId = selectedEnvId;
            // `datasource` is set to the result of the `getDatabaseRollbackData` method with null as the argument
            const datasource = this.getDatabaseRollbackData(null);
            // The server-side datasource for the ag-Grid is set to `datasource`
            this.api?.setServerSideDatasource(datasource);
        }
        else {
            // Set the "isEnvironmentSelected" variable to false
            this.isEnvironmentSelected = false;
            // Set the "selectedEnvironmentId" variable to an empty string
            this.selectedEnvironmentId = '';
        }
    }
    /**
     * Sets the page size for pagination in the ag-Grid.
     * @returns void
     */
    setPageSize() {
        // The page size for pagination in the ag-Grid is set to `pageSize`
        this.api.paginationSetPageSize(this.pageSize);
    }

    /**
   * Sets the loader state to indicate that a loading process has started.
   * Also displays the loading spinner.
   */
    setLoader() {
        // `loading` is set to true to indicate that a loading process has started
        this.loading = true;
        // `showSpinner.next(true)` is called on `loaderService` to display the loading spinner
        this.loader.showSpinner.next(true);
    }

    /**
     * Sets the loader state to indicate that the loading process has ended.
     * Hides the loading spinner.
     */
    setUnLoader() {
        // `loading` is set to false to indicate that the loading process has ended
        this.loading = false;
        // `showSpinner.next(false)` is called on `loaderService` to hide the loading spinner
        this.loader.showSpinner.next(false);
    }

    /**
     * Closes the rollback migration details slider and resets its width.
     */
    closerollbackMigrationDetails() {
        this.showRollbackMigrationDetailsSlider = false;
        // show conversation History slider
        setTimeout(() => {
            // Get the element with the id 'rollbackMigrationDetailsSliderId' and set its width to '0px'
            const sliderElement = document.getElementById(this.rollbackMigrationDetailsSliderId);
            if (sliderElement) {
                sliderElement.style.width = this.constantsService.sliderResetWidth;
            }
        }, 200);
    }
}